import moment, { Moment } from 'moment'

export type BranchSchedule = { [day: string]: string }

export const DEFAULT_HOURS_START = 6
export const DEFAULT_HOURS_END = 18
export const SLOT_MINUTES = 15
const MAX_LOOKAHEAD_DAYS = 14
const WEEKDAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

type Window = { start: number; end: number }

const FALLBACK_WINDOW: Window = {
  start: DEFAULT_HOURS_START * 60,
  end: (DEFAULT_HOURS_END + 1) * 60,
}

export const scheduleKey = (day: Moment): string =>
  WEEKDAY_KEYS[day.isoWeekday() - 1]

export const parseCutoffMinutes = (cutoff?: string | null): number | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec((cutoff ?? '').trim())
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null
  return hours * 60 + minutes
}

export const clampLeadDays = (
  leadTimeDays?: number | string | null
): number => {
  const days = parseInt(String(leadTimeDays ?? ''), 10)
  return Number.isFinite(days) && days > 0 ? Math.min(days, 365) : 0
}

const minutesOfDay = (time: Moment) => time.hours() * 60 + time.minutes()

const ceilToSlot = (minutes: number) =>
  Math.ceil(minutes / SLOT_MINUTES) * SLOT_MINUTES

const nextSlotFrom = (now: Moment) =>
  ceilToSlot(minutesOfDay(now) + (now.seconds() > 0 ? 1 : 0))

const parseWindow = (range?: string): Window | null => {
  if (!range) return null
  const [from, to] = range.split(' - ')
  const start = moment(from, 'HH:mm:ss', true)
  const end = moment(to, 'HH:mm:ss', true)
  if (!start.isValid() || !end.isValid()) return null
  const window = { start: minutesOfDay(start), end: minutesOfDay(end) }
  return window.end > window.start ? window : null
}

const openWindows = (schedules: BranchSchedule[], day: Moment): Window[] =>
  schedules
    .map((schedule) => parseWindow(schedule?.[scheduleKey(day)]))
    .filter((window): window is Window => window !== null)

const unionOf = (windows: Window[]): Window => ({
  start: Math.min(...windows.map((w) => w.start)),
  end: Math.max(...windows.map((w) => w.end)),
})

const hasAnySchedule = (schedules: BranchSchedule[]) =>
  schedules.some((schedule) => schedule && Object.keys(schedule).length > 0)

const isOpenAt = (windows: Window[], minute: number) =>
  windows.some((w) => minute >= w.start && minute < w.end)

const firstSlotOfDay = (
  now: Moment,
  day: Moment,
  schedules: BranchSchedule[]
): Moment | null => {
  const open = openWindows(schedules, day)
  const windows = open.length
    ? open
    : hasAnySchedule(schedules)
    ? []
    : [FALLBACK_WINDOW]
  if (!windows.length) return null
  const union = unionOf(windows)
  let minute = ceilToSlot(union.start)
  if (day.isSame(now, 'day')) minute = Math.max(minute, nextSlotFrom(now))
  for (; minute + SLOT_MINUTES <= union.end; minute += SLOT_MINUTES) {
    if (isOpenAt(windows, minute)) {
      return day.clone().startOf('day').add(minute, 'minutes')
    }
  }
  return null
}

export type EarliestPickup = { minDate: Moment; defaultSlot: Moment }

export const getEarliestPickup = ({
  now,
  leadTimeDays,
  cutoff,
  schedules,
}: {
  now: Moment
  leadTimeDays?: number | string | null
  cutoff?: string | null
  schedules: BranchSchedule[]
}): EarliestPickup => {
  const cutoffMinutes = parseCutoffMinutes(cutoff)
  const afterCutoff =
    cutoffMinutes !== null && minutesOfDay(now) > cutoffMinutes
  const firstDay = now
    .clone()
    .startOf('day')
    .add(clampLeadDays(leadTimeDays) + (afterCutoff ? 1 : 0), 'days')
  for (let offset = 0; offset <= MAX_LOOKAHEAD_DAYS; offset += 1) {
    const slot = firstSlotOfDay(
      now,
      firstDay.clone().add(offset, 'days'),
      schedules
    )
    if (slot) return { minDate: slot.clone().startOf('day'), defaultSlot: slot }
  }
  return {
    minDate: firstDay.clone(),
    defaultSlot: firstDay.clone().add(FALLBACK_WINDOW.start, 'minutes'),
  }
}

export type HourRange = {
  hoursStart: number
  hoursEnd: number
  minutesStart: number
}

export const getHourRange = (
  now: Moment,
  day: Moment,
  schedules: BranchSchedule[],
  selectedHour?: number
): HourRange => {
  const open = openWindows(schedules, day)
  const union = open.length ? unionOf(open) : FALLBACK_WINDOW
  const lastSlot = union.end - SLOT_MINUTES
  let firstSlot = ceilToSlot(union.start)
  if (day.isSame(now, 'day')) firstSlot = Math.max(firstSlot, nextSlotFrom(now))
  if (firstSlot > lastSlot) firstSlot = lastSlot
  const hoursStart = Math.floor(firstSlot / 60)
  return {
    hoursStart,
    hoursEnd: Math.floor(lastSlot / 60),
    minutesStart: selectedHour === hoursStart ? firstSlot % 60 : 0,
  }
}

// Company-level guard for an already-stored pickUpTime: it must still lie on or after
// the earliest day the company's lead-time rule allows (lead days, +1 after the same-day
// cutoff) and must not be in the past. Schedules are irrelevant here — they only shape
// slots within a day, and the stored slot was valid when picked. Unparseable input is
// disallowed; missing config falls back to the permissive defaults (0 days, no cutoff
// passed = today allowed), so an unloaded store never traps the customer in the wizard.
export const isPickupTimeAllowed = ({
  now,
  pickUpTime,
  leadTimeDays,
  cutoff,
}: {
  now: Moment
  pickUpTime?: string | null
  leadTimeDays?: number | string | null
  cutoff?: string | null
}): boolean => {
  if (!pickUpTime) return false
  const time = moment(pickUpTime)
  if (!time.isValid() || time.isBefore(now)) return false
  const cutoffMinutes = parseCutoffMinutes(cutoff)
  const afterCutoff =
    cutoffMinutes !== null && minutesOfDay(now) > cutoffMinutes
  const minDay = now
    .clone()
    .startOf('day')
    .add(clampLeadDays(leadTimeDays) + (afterCutoff ? 1 : 0), 'days')
  return !time.clone().startOf('day').isBefore(minDay)
}
